// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// MEETING 2 — the specification session, 29 July 2026.
//
// 1h44 with Dan, transcribed on the Falcon box (ivrit-ai large-v3-turbo, 101
// minutes of compute, 1,383 lines). This box is the SCAN layer: the handful of
// things that change what we build. The full read lives at
// kb-doc/consultancy/falcon/meeting-02 — per the board rule, if you READ it it
// is a document, if you SCAN it it stays a box.
//
// The right-hand panel is a copy-paste recap for Dan, in Hebrew, because the
// note goes to him and not to us. Erez asked for it as a box he can copy from,
// so it is selectable text with a copy button rather than an image or a card.
//
// Owner: Otto.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";

const C = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", hair: "#E3DED4", paper: "#FAF7F2", bone: "#F2EDE4",
  green: "#3F8F6F", plum: "#7A5B78", amber: "#B07A1E", red: "#C0492F",
  blue: "#2F6BB0", petrol: "#2C7E9E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// The note to Dan. Hebrew, because he is the reader. Short but not thin —
// Erez: "short and to the point. Not too short."
//
// TWO CORRECTIONS FROM EREZ, 2026-07-29, both worth keeping:
//   1. THE CLIENT-FACING BRAND IS **TAILORMIND**, NOT MYJARVIS. MyJarvis is the
//      internal OS. Anything a client reads says Tailormind.
//   2. NEVER PUT HEBREW AND LATIN ON THE SAME LINE IN AN RTL BLOCK. The header
//      was "סיכום פגישה — פלקון × MyJarvis, 29 ביולי" and RTL reordering turned
//      it into "Falcon, 29, MyJarvis, July" on screen. One direction per line.
const DAN_NOTE = `סיכום שיחה
29 ביולי 2026
Tailormind × Falcon

דן, תודה על פגישה מצוינת ומאוד פרודוקטיבית. הנה התמצית:

■ מה עשינו
אפיינת את מאגר הנכסים שדה אחר שדה. הזהות של נכס = בניין + קומה + שטח, ואחריה: חלוקה, בעלים, מסגרות זמנים (מועד פינוי והגמישות סביבו, ובנפרד — אורך העסקה שהבעלים רוצה), מחיר ואם הוא נתון למו״מ, הוצאות נלוות בדגש על דמי ניהול, חניות, תמונות ותוכנית חלוקה, וכיוון חזית. כל פלט יוצא עם ״המחירים אינם כוללים מע״מ״.

■ מה הבנו שהוא הלב
העבודה שלך היא איסוף מידע, הגנה על מידע וניהול מידע — לא שיווק. לכן המערכת נבנית סביב זה: מאגר שמחזיק הכול ונשלף בקול, שמירה על זהות הנכס עד שהלקוח חתום, ותיעוד מסודר ומתוארך של מה הוצע למי ומתי — כי זה מה שמגן עליך.

■ דברים חדשים שעלו והוספנו לתכולה
• מעקב אוטומטי אחרי קבוצות פייסבוק ולוחות, עם התראה על כל נכס רלוונטי שעולה
• חיבור לתיבת המייל — מיילים כמו של עופר מכלל נקלטים למאגר לבד
• זריקת PDF מקולגה, ונכנס בפורמט שלנו
• זיהוי נכס מתוך תמונה
• הודעות יזומות לשמירה על קשר מול בעלי נכסים וחברות ניהול
• One-pager אחיד להעברה ללקוח, לוואטסאפ או למייל

■ מה הלאה
1. בשבוע הקרוב — אתה מקבל אייג׳נט עובד בטלגרם ומתחילים לבדוק אותו על נכסים ולקוחות אמיתיים.
2. השבוע שאחריו — שבוע צפוף של תיקונים ושיפורים לפי מה שיעלה מהשימוש.
3. מיתוג, אתר ושיווק — נדחים לבקשתך, אחרי שהמערכת עומדת.

תודה רבה,`;

type Item = { tag: string; tone: string; title: string; body: string };

const HEADLINES: Item[] = [
  { tag: "The one line", tone: C.petrol, title: "“Half my work is business espionage”",
    body: "His own framing, and it reframes the product: <b>“my work is not really marketing — it is extracting information, protecting it, and managing it digitally.”</b> Then the admission: <b>“the heavy workflow I am bad at is a database on relationships, a database on clients, and matching between them.”</b> He is good at the human half and says so; the gap is everything upstream." },
  { tag: "Design constraint", tone: C.red, title: "Voice-first is a requirement, not a preference",
    body: "Dan stated twice, matter-of-factly, that <b>he does not have his right hand</b> — “I cannot perform any action except talking to people and being present with them.” Everything we already chose (voice in, voice out, Telegram, no forms) is now <b>non-negotiable</b>, and should be recorded that way so nobody trades it for a form later." },
  { tag: "The schema", tone: C.green, title: "He dictated the property record himself",
    body: "The identity is a <b>triple</b>: building → floor → size within the floor. “Migdal Sapir, floor 30, 480 of the 1,600 — once I know that, I know the identity.” Then layout, owner, <b>timeframes split in two</b> (when it frees + flexibility, and separately the lease length the owner wants — “nobody talks about it, and it is one of the most important parameters”), price + negotiability, associated costs <b>led by management fees</b> (“in this building 27, in that one 18 — ten shekels ends up being real money”), parking, photos, floor plan." },
  { tag: "We had it wrong", tone: C.amber, title: "Inventory arrives by push, not by legwork",
    body: "He corrected us: “I hardly go and discover properties door-to-door.” It arrives by <b>email and WhatsApp from distribution lists he is already on</b> — a live example landed mid-meeting from Clal Insurance. Plus colleague brokers. And the sharp one: <b>he extracts properties from his own clients without them realising</b> — asks what they have seen, they list 20, he knows 15, and he has gained 5." },
  { tag: "New scope", tone: C.plum, title: "Four asks that were not in discovery",
    body: "<b>1 · Watch the groups he is not in.</b> “Something that really bothers me and I have not told you until today” — properties post daily on Facebook groups and Yad2; he stopped looking 3–4 years ago. He wants a push per find. <b>This is a scheduled monitor, not the chat agent.</b> <b>2 · Keep relationships warm</b> with periodic personal WhatsApp touches — and he is fine with it being obviously AI: “I am not hiding behind it.” <b>3 · Eat the inbox and PDFs.</b> <b>4 · Identify a property from a photo.</b>" },
  { tag: "The hard part", tone: C.blue, title: "The vagueness ladder, and why the one-pager matters",
    body: "His fear is <b>“cutting the middleman”</b> — “it happens all the time.” The ID <i>is</i> the asset, so before a client signs he <b>describes without naming</b>: “first we convey who Moshe is without calling him Moshe.” That day he had given <i>Gibor Sport, 460m</i> — deliberately not floor 23. <b>Photos leak identity too</b>: a tenant name in an image gives the building away. So client-facing output must default to withholding the identifying detail." },
  { tag: "He taught us", tone: C.ink, title: "How a broker actually gets paid",
    body: "Three things, all required, before “were you the effective cause” can even be asked: a valid licence, the annual fee paid, and <b>a written brokerage-services order</b>. <b>Verbal has no legal meaning.</b> But he will not send a contract up front — “it scares clients away”, and insisting on it at his old firm “destroyed a lot”. The light mechanism that works: <b>an email listing the properties, asking the client to reply confirming they know them through him.</b> “That is exactly what saved me in the Trillion deal.” Protection lapses after <b>6 months</b> — which is why everything must be timestamped." },
  { tag: "Agreed", tone: C.green, title: "Next week it goes live — and Dan deferred branding",
    body: "<b>“Next week is already testing. It is live, we start using it.”</b> Then a concentrated week of fixes. He wants to load it himself in the evenings before we extract any output. And <b>he postponed branding and the website himself</b>: “I have nothing to draw more leads with before I organise my swords — maybe the whole first month.” Requirement 4 stands, but not now." },
];

function Card({ it }: { it: Item }) {
  return (
    <div data-zoom style={{
      width: 520, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderTop: `4px solid ${it.tone}`, borderRadius: 18,
      boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT,
      display: "flex", flexDirection: "column", gap: 9,
    }}>
      <div style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.13em",
                    textTransform: "uppercase", color: it.tone }}>{it.tag}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.ink, lineHeight: 1.22,
                    letterSpacing: "-0.01em" }}>{it.title}</div>
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: C.ink2 }}
           dangerouslySetInnerHTML={{ __html: it.body }} />
    </div>
  );
}

function DanNote() {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(DAN_NOTE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };
  return (
    <div data-zoom style={{
      width: 560, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderTop: `4px solid ${C.green}`, borderRadius: 18,
      boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.13em",
                        textTransform: "uppercase", color: C.green }}>Send to Dan</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: C.ink, lineHeight: 1.22, marginTop: 4 }}>
            The recap, ready to paste
          </div>
        </div>
        {/* Was unclickable by MOUSE since the day it shipped: without
            `nopan nodrag` + stopping pointerdown, React Flow captures the
            pointer on mousedown to start a pan and no click event is ever
            fired on the button. Found while testing the email-template
            panel, which copied this pattern. */}
        <button
          className="nopan nodrag"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={copy}
          style={{
            fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "#fff", background: copied ? C.petrol : C.ink,
            border: "none", borderRadius: 999, padding: "8px 16px", cursor: "pointer",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.5, color: C.faint }}>
        Hebrew, because Dan is the reader. Covers what was specified, what we understood to be the
        real problem, the new scope, and the agreed sequence — including that he postponed branding himself.
      </div>
      {/* Selectable, RTL, monospace-free: he may paste this into WhatsApp or email.
          SCROLLING: `nowheel` is React Flow's own opt-out — without it the canvas
          swallows the wheel event to pan the board and this panel never scrolls
          (exactly what happened on the first pass). The stopPropagation is a
          belt-and-braces for the drag-to-pan handler on the same container. */}
      <pre
        className="nowheel"
        onWheel={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          margin: 0, direction: "rtl", textAlign: "right", whiteSpace: "pre-wrap",
          wordBreak: "break-word", fontFamily: FONT, fontSize: 14, lineHeight: 1.6,
          color: C.ink, background: C.paper, border: `1px solid ${C.hair}`,
          borderRadius: 12, padding: "18px 20px", userSelect: "text", maxHeight: 560,
          overflowY: "auto", overscrollBehavior: "contain", cursor: "text",
        }}>{DAN_NOTE}</pre>
    </div>
  );
}

export function MeetingTwo() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontFamily: FONT, fontSize: 14.5, lineHeight: 1.55, color: C.ink2, maxWidth: 1240 }}>
        <b>1h 44m, 29 July.</b> Not a discovery conversation — a <b>specification session</b>. Dan dictated the
        property record field by field, gave us his unit economics, corrected our assumption about where his
        inventory comes from, and taught us the legal mechanics of how a broker actually gets paid. Transcribed on
        the Falcon box in Hebrew; the full read is at <code>kb-doc/consultancy/falcon/meeting-02</code>.
        <span style={{ color: C.faint }}> Whisper does not separate speakers, so attribution is inferred from
        content — the audio is kept and timestamps are global, so anything contested can be checked.</span>
      </div>
      <div style={{ display: "flex", gap: 26, alignItems: "flex-start" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 520px)", gap: 26 }}>
          {HEADLINES.map((it) => <Card key={it.title} it={it} />)}
        </div>
        <DanNote />
      </div>
    </div>
  );
}
